import React, { useState } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { mapBy } from '../../../../../../../../shared/lib/functional';

import RectangleCard from '../../components/RectangleCard';
import Alert from '../../components/Alert';
import Pagination from '../../components/Pagination';
import ExperienceSummary from '../../components/ExperienceSummary';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import { useForcedCartCache } from '../../context/CartCacheContext';
import { useForcedEventCache } from '../../context/EventCacheContext';
import { StepPropsBase } from '../Reserve';
import { evaluateAudienceZone } from '../../../../../../../../shared/lib/audience';

type AddonsViewProps = StepPropsBase;

export default function AddonsView(props: AddonsViewProps) {
  const { onBack, onNext } = props;

  const { event, allEvents } = useForcedEventCache();
  const { activeDraft: activeDraft, passiveUpdateAddon, saveCart } = useForcedCartCache();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const draft = activeDraft.reservationDraft;
  const draftTemplate = draft.eventSnapshot;

  const ticketsSum = (draft.tickets ?? []).map(t => t.quantity).reduce((a, b) => a + b, 0);
  const availableAddons = draftTemplate?.reservables.addons ?? [];
  const itemsPerPage = 12;
  const totalPages = Math.ceil(availableAddons.length / itemsPerPage);
  const paginatedAddons = availableAddons.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const myAddons = draft.addons ?? [];
  const selectedAddonCount = myAddons.reduce((sum, addonSelection) => {
    return sum + (addonSelection.quantity || 0);
  }, 0);
  const requiresAddon = !!draftTemplate?.reservables.requireAtLeastOneAddon;
  const canContinue = !requiresAddon || selectedAddonCount > 0;

  const myAddonsDict = mapBy(myAddons, a => a.addon._id);
  const eventAddons = mapBy(event.eventTemplate?.reservables.addons ?? [], '_id');

  async function handleOnNext() {
    setLoading(true);

    try {
      if (!canContinue) return;
      await saveCart();
      onNext?.();
    } finally {
      setLoading(false);
    }
  }

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const visibleZones = dataCollections.zones
    .filter(z => evaluateAudienceZone('visible', z._id, undefined, event.eventTemplate.audience))
    .map(z => (
      <div
        key={z._id}
        className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
        style={{ backgroundColor: z.color }}
      >
        {z.name}
      </div>
    ));

  return (
    <TwoColumnLayout
      title={draftTemplate?.title}
      tags={visibleZones}
      subtitle="Addons"
      aside={
        <ExperienceSummary
          showDisclaimer
          nextDisabled={!canContinue}
          onBack={() => onBack?.()}
          onNext={handleOnNext}
          loading={loading}
          nextIconPosition="prefix"
          nextIcon={['far', 'cart-shopping']}
        />
      }
    >
      {requiresAddon && selectedAddonCount < 1 && (
        <Alert>You must select at least one addon to continue.</Alert>
      )}
      {availableAddons.length > 0 ? (
        <>
          <div className="grid gap-4 grid-cols-1">
            {paginatedAddons.map(a => (
              <RectangleCard
                key={a._id}
                name={a.name}
                description={a.description}
                price={a.price * (a.pricePerTicket ? ticketsSum : 1)}
                image={eventAddons[a._id]?.images[0]}
                amount={myAddonsDict[a._id]?.quantity}
                setAmount={amt => passiveUpdateAddon(a, amt)}
              />
            ))}
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPage={setCurrentPage} />
        </>
      ) : (
        <Alert>
          {requiresAddon
            ? 'At least one addon is required, but no addons are currently available for this experience.'
            : 'No addons available for this experience. You can continue to the next step.'}
        </Alert>
      )}
    </TwoColumnLayout>
  );
}
